/**
 * tire-size-finder.js
 *
 * Buscador de tallas ISSE. La talla se CALCULA con la misma fórmula del
 * Excel "ISSE Size Finder" (SF USA 2013):
 *   altura flanco (cm) = ancho(mm) * perfil(%) / 1000
 *   diámetro (cm)      = 2 * altura flanco + radio(pulgadas) * 2,54
 *   perímetro (cm)     = 3,1416 * diámetro
 * y la talla es el rango (TALLA_RANGES) en el que cae el perímetro.
 *
 * - Width/Profile/Radius son campos de texto libres.
 * - Si el perímetro cae fuera de 155 - 410,99 cm (o los valores no son
 *   números), se muestra "Size not found...". El error solo aparece al
 *   pulsar Search; mientras se escribe solo se actualiza el resultado.
 * - El código en letra / especial (XS, S, M... 273... 416) sale del
 *   metaobject isse_special_size_code (talla -> código).
 */

(function () {
  'use strict';

  function formatNumber(n) {
    // 17.5 -> "17,5" ; 18 -> "18"  (convención europea usada en la tabla ISSE)
    return Number.isInteger(n) ? String(n) : String(n).replace('.', ',');
  }

  // Rangos de perímetro (cm) -> talla, idénticos a los del Excel.
  // Se usa el límite inferior de cada rango (sin huecos entre rangos).
  var TALLA_RANGES = [
    { min: 155, talla: 54 },  { min: 174, talla: 58 },  { min: 186, talla: 62 },
    { min: 200, talla: 66 },  { min: 215, talla: 70 },  { min: 234, talla: 74 },
    { min: 256, talla: 78 },  { min: 266, talla: 80 },  { min: 272, talla: 88 },
    { min: 279, talla: 90 },  { min: 287, talla: 92 },  { min: 291, talla: 94 },
    { min: 313, talla: 96 },  { min: 320, talla: 98 },  { min: 330, talla: 100 },
    { min: 339, talla: 102 }, { min: 343, talla: 104 }, { min: 349, talla: 106 },
    { min: 355, talla: 108 }, { min: 363, talla: 110 }
  ];
  var PERIMETER_MIN = 155;
  var PERIMETER_MAX = 410.99;

  function calcPerimeterCm(w, p, r) {
    var sidewallCm = (w * p) / 1000;
    var diameterCm = sidewallCm * 2 + r * 2.54;
    return 3.1416 * diameterCm;
  }

  // El metaobject isse_special_size_code mezcla dos tipos de entrada con la
  // misma talla: códigos de la cadena (XS, S, 273, 27/3...) y medidas
  // especiales de camión/bus (8,5-17,5 · 9R20 · 35/12,5R20 · 10.00-20...).
  // Las medidas de camión siempre llevan "R" o "-"; los códigos nunca.
  function isTruckMeasure(codigo) {
    return /[Rr-]/.test(String(codigo));
  }

  function buildState(rawData) {
    var specials = (rawData.specials || []).filter(function (s) {
      return s.codigo && s.talla;
    });
    var i18n = rawData.i18n || {};
    return { specials: specials, i18n: i18n };
  }

  // Devuelve { talla } o null si el perímetro queda fuera de rango.
  function findTalla(state, w, p, r) {
    var perimeter = calcPerimeterCm(w, p, r);
    if (!(perimeter >= PERIMETER_MIN && perimeter <= PERIMETER_MAX)) return null;
    var found = null;
    for (var i = 0; i < TALLA_RANGES.length; i++) {
      if (perimeter >= TALLA_RANGES[i].min) found = TALLA_RANGES[i];
    }
    return found ? { talla: found.talla } : null;
  }

  // Acepta coma o punto como separador decimal (ej. "17,5" o "17.5") y
  // devuelve NaN si el texto no es un número válido.
  function parseFieldValue(rawValue) {
    if (!rawValue) return NaN;
    var normalized = String(rawValue).trim().replace(',', '.');
    if (normalized === '') return NaN;
    return Number(normalized);
  }

  function init(sectionId) {
    var root = document.getElementById(sectionId);
    if (!root) return;

    var dataScript = document.getElementById('TireSizeData-' + sectionId.replace('TireSizeFinder-', ''));
    if (!dataScript) return;

    var rawData;
    try {
      rawData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('TireSizeFinder: no se pudieron parsear los datos', e);
      return;
    }

    var state = buildState(rawData);

    var widthSelect = root.querySelector('[data-role="width"]');
    var profileSelect = root.querySelector('[data-role="profile"]');
    var radiusSelect = root.querySelector('[data-role="radius"]');
    var searchBtn = root.querySelector('[data-role="search"]');
    var resultEl = root.querySelector('[data-role="result"]');
    var resultBadge = root.querySelector('[data-role="result-badge"]');
    var resultSizeEl = root.querySelector('[data-role="result-size"]');
    var resultArrow = root.querySelector('[data-role="result-arrow"]');
    var specialSelect = root.querySelector('[data-role="special-select"]');
    var specialResult = root.querySelector('[data-role="special-result"]');

    var idSuffix = sectionId.replace('TireSizeFinder-', '');
    var modal = document.getElementById('TireSizeModal-' + idSuffix);
    var modalOverlay = modal ? modal.querySelector('[data-role="modal-overlay"]') : null;
    var modalClose = modal ? modal.querySelector('[data-role="modal-close"]') : null;
    var cardNumeric = modal ? modal.querySelector('[data-role="card-numeric"]') : null;
    var cardNumericSize = modal ? modal.querySelector('[data-role="card-numeric-size"]') : null;
    var cardNumericCta = modal ? modal.querySelector('[data-role="card-numeric-cta"]') : null;
    var cardLetter = modal ? modal.querySelector('[data-role="card-letter"]') : null;
    var cardLetterSize = modal ? modal.querySelector('[data-role="card-letter-size"]') : null;
    var cardLetterCta = modal ? modal.querySelector('[data-role="card-letter-cta"]') : null;
    var modalCardsEl = modal ? modal.querySelector('[data-role="modal-cards"]') : null;

    var numericCollectionUrl = modalCardsEl ? modalCardsEl.getAttribute('data-numeric-collection') : '';
    var letterCollectionUrl = modalCardsEl ? modalCardsEl.getAttribute('data-letter-collection') : '';
    var sizeOptionName = (modalCardsEl && modalCardsEl.getAttribute('data-size-option')) || 'Size';

    function buildFilteredUrl(baseUrl, value) {
      if (!baseUrl) return null;
      var separator = baseUrl.indexOf('?') === -1 ? '?' : '&';
      var param = 'filter.v.option.' + encodeURIComponent(sizeOptionName);
      return baseUrl + separator + param + '=' + encodeURIComponent(value);
    }

    var cardNumericModels = modal ? modal.querySelector('[data-role="card-numeric-models"]') : null;
    var cardNumericLabel = cardNumeric ? cardNumeric.querySelector('.tire-size-modal__card-label') : null;
    var defaultNumericLabel = cardNumericLabel ? cardNumericLabel.textContent : '';

    // isTruck: búsqueda desde Special size (medidas de camión/bus) -> una sola
    // tarjeta con el texto literal "MODEL : TRUCK", sin la tarjeta de letras.
    function openModal(talla, isApprox, isTruck) {
      if (!modal) return;

      if (cardNumericLabel) {
        cardNumericLabel.textContent = isTruck ? 'MODEL : TRUCK' : defaultNumericLabel;
      }
      if (cardNumericModels) cardNumericModels.hidden = !!isTruck;

      if (cardNumeric && cardNumericSize) {
        cardNumericSize.textContent = isApprox
          ? talla + ' ' + (state.i18n.approx_suffix || '(approximate)')
          : talla;
        cardNumeric.hidden = false;

        if (cardNumericCta) {
          // Los valores de variante numéricos van con ".0" (ej. "54.0"), tal como
          // aparecen en las opciones de variante de los productos de ISSE.
          var numericUrl = buildFilteredUrl(numericCollectionUrl, Number(talla).toFixed(1));
          if (numericUrl) {
            cardNumericCta.href = numericUrl;
            cardNumericCta.hidden = false;
          } else {
            cardNumericCta.hidden = true;
          }
        }
      }

      // Tarjeta de código: ignora las medidas de camión (solo van en el desplegable).
      var specialMatch = isTruck ? null : state.specials.find(function (s) {
        return Number(s.talla) === Number(talla) && !isTruckMeasure(s.codigo);
      });

      if (cardLetter && cardLetterSize) {
        if (specialMatch) {
          cardLetterSize.textContent = specialMatch.codigo;
          cardLetter.hidden = false;

          if (cardLetterCta) {
            var letterUrl = buildFilteredUrl(letterCollectionUrl, specialMatch.codigo);
            if (letterUrl) {
              cardLetterCta.href = letterUrl;
              cardLetterCta.hidden = false;
            } else {
              cardLetterCta.hidden = true;
            }
          }
        } else {
          cardLetter.hidden = true;
        }
      }

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      if (!modal) return;
      modal.hidden = true;
      document.body.style.overflow = '';
    }

    if (modalOverlay) modalOverlay.addEventListener('click', closeModal);
    if (modalClose) modalClose.addEventListener('click', closeModal);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    var exampleSizeText = resultSizeEl.textContent;

    function resetResult() {
      resultEl.hidden = true;
      resultEl.textContent = '';
      resultBadge.classList.add('is-example');
      resultBadge.classList.remove('is-approx');
      if (resultArrow) {
        resultArrow.classList.add('is-example');
        resultArrow.classList.remove('is-approx');
      }
      resultSizeEl.textContent = exampleSizeText;
    }

    function updateSearchButtonState() {
      searchBtn.disabled = !(widthSelect.value.trim() && profileSelect.value.trim() && radiusSelect.value.trim());
    }

    function showResult(w, p, r, openModalToo, showErrorOnFail) {
      if (!w || !p || !r || isNaN(w) || isNaN(p) || isNaN(r)) {
        if (showErrorOnFail) {
          resultEl.hidden = false;
          resultEl.textContent = state.i18n.error_calculating || 'Error calculating. Check the values and try again.';
        }
        return;
      }

      var match = findTalla(state, w, p, r);

      if (!match) {
        resetResult();
        if (showErrorOnFail) {
          resultEl.hidden = false;
          resultEl.textContent = state.i18n.not_found || 'Size not found. Check the values and try again.';
        }
        return;
      }

      resultEl.hidden = true;
      resultBadge.classList.remove('is-example', 'is-approx');
      if (resultArrow) {
        resultArrow.classList.remove('is-example', 'is-approx');
      }
      resultSizeEl.textContent =
        formatNumber(w) + '/' + formatNumber(p) + 'R' + formatNumber(r);

      if (openModalToo) {
        openModal(match.talla, false);
      }

      // Notifica a otros scripts de la tienda (p.ej. para filtrar productos
      // por talla, o abrir un modal con la ficha de la cadena correspondiente).
      root.dispatchEvent(
        new CustomEvent('tire-size-finder:result', {
          bubbles: true,
          detail: { width: w, profile: p, radius: r, talla: match.talla },
        })
      );
    }

    // Actualiza en vivo (con un pequeño margen tras dejar de teclear) en cuanto
    // los 3 campos tienen contenido, sin esperar al clic en "Buscar". El modal
    // solo se abre con el botón, para no interrumpir mientras el usuario escribe.
    var liveUpdateTimer = null;
    function scheduleLiveUpdate() {
      updateSearchButtonState();
      if (liveUpdateTimer) clearTimeout(liveUpdateTimer);
      if (!widthSelect.value.trim() || !profileSelect.value.trim() || !radiusSelect.value.trim()) {
        resetResult();
        return;
      }
      liveUpdateTimer = setTimeout(function () {
        var w = parseFieldValue(widthSelect.value);
        var p = parseFieldValue(profileSelect.value);
        var r = parseFieldValue(radiusSelect.value);
        showResult(w, p, r, false, false);
      }, 500);
    }

    widthSelect.addEventListener('input', scheduleLiveUpdate);
    profileSelect.addEventListener('input', scheduleLiveUpdate);
    radiusSelect.addEventListener('input', scheduleLiveUpdate);

    searchBtn.addEventListener('click', function () {
      if (liveUpdateTimer) clearTimeout(liveUpdateTimer);
      var w = parseFieldValue(widthSelect.value);
      var p = parseFieldValue(profileSelect.value);
      var r = parseFieldValue(radiusSelect.value);
      showResult(w, p, r, true, true);
    });

    function populateSpecialSelect() {
      if (!specialSelect) return;
      specialSelect.innerHTML = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Special Sizes';
      specialSelect.appendChild(placeholder);
      // El desplegable lista solo las medidas especiales de camión/bus.
      // Los códigos (XS... 416) se quedan en state.specials para la tarjeta del modal.
      state.specials
        .filter(function (s) { return isTruckMeasure(s.codigo); })
        .forEach(function (s) {
          var o = document.createElement('option');
          o.value = String(s.talla);
          o.textContent = s.codigo;
          specialSelect.appendChild(o);
        });
    }

    // El botón Search del panel Special size comparte data-role="search" con el
    // principal, así que se localiza dentro del panel.
    var specialBtn = root.querySelector('[data-role="special-panel"] [data-role="search"]');

    function updateSpecialButtonState() {
      if (specialBtn) specialBtn.disabled = !(specialSelect && specialSelect.value);
    }

    if (specialSelect) {
      specialSelect.addEventListener('change', function () {
        if (specialResult) {
          specialResult.value = specialSelect.value || '0';
        }
        updateSpecialButtonState();
      });
    }

    if (specialBtn) {
      specialBtn.addEventListener('click', function () {
        if (!specialSelect || !specialSelect.value) return;
        openModal(Number(specialSelect.value), false, true);
      });
    }

    updateSearchButtonState();
    populateSpecialSelect();
  }

  window.TireSizeFinder = { init: init };
})();